// c:\Users\Parashuram\Projects\ei-demand-supply-tool-Parashuram-branch\Cognizant\client\app\src\pages\Dashboard.js
import React, { useState, useEffect, useRef } from "react";
import { Typography, Box, Backdrop, CircularProgress, Switch, FormControlLabel, Popper, Paper, Fade, IconButton, Tooltip, Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ImageIcon from "@mui/icons-material/Image";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DashboardTable from "../components/DashboardTable";
import FilterControls from "../components/FilterControls";
import DashboardCharts from "./DashboardCharts";

function Dashboard() {
  const [reportData, setReportData] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [chartView, setChartView] = useState(() => {
    const saved = localStorage.getItem("dashboardChartView");
    return saved === "true";
  });
  const [loading, setLoading] = useState(() => {
    // If chartView is enabled, don't show loading initially
    const savedChartView = localStorage.getItem("dashboardChartView");
    return savedChartView !== "true";
  });
  const [showTour, setShowTour] = useState(() => {
    const savedChartView = localStorage.getItem("dashboardChartView");
    const tourSeen = localStorage.getItem("dashboardTourSeen");
    return savedChartView !== "true" && tourSeen !== "true";
  });
  const toggleRef = useRef(null);
  const tableRef = useRef(null);
  const primaryColor = "#005EB8";

  // Export handlers
  const handleExportExcel = () => {
    if (tableRef.current) {
      tableRef.current.exportExcel();
    }
  };

  const handleExportImage = () => {
    if (tableRef.current) {
      tableRef.current.exportImage();
    }
  };

  const handleCopyData = () => {
    if (tableRef.current) {
      tableRef.current.copyData();
    }
  };

  // Auto-close tour after 5 seconds
  useEffect(() => {
    if (showTour) {
      const timer = setTimeout(() => {
        setShowTour(false);
        localStorage.setItem("dashboardTourSeen", "true");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showTour]);

  const closeTour = () => {
    setShowTour(false);
    localStorage.setItem("dashboardTourSeen", "true");
  };

  const handleChartViewToggle = (event) => {
    const newValue = event.target.checked;
    setChartView(newValue);
    localStorage.setItem("dashboardChartView", newValue.toString());
    // When switching to chart view, hide the loading
    if (newValue) {
      setLoading(false);
    }
  }; 
  
  const handleReportData = (data, filters) => {
    setReportData(data);
    setFilterValues(filters);
  };

  const handleLoadingChange = (isLoading) => {
    setLoading(isLoading);
  };

  // AI Analysis state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const generateAiAnalysis = () => {
    if (!reportData || reportData.length === 0) return "";
    
    // Find grand total row
    const firstColKey = Object.keys(reportData[0])[0];
    const grandTotalRow = reportData.find(row => 
      String(row[firstColKey]).toLowerCase().includes('grand total')
    );
    
    if (!grandTotalRow) return "No data available for analysis.";
    
    // Get values dynamically
    const keys = Object.keys(grandTotalRow);
    const totalDemandKey = keys.find(k => k.toLowerCase().replace(/[_\s]/g, '').includes('totaldemand'));
    const totalInternalKey = keys.find(k => k.toLowerCase().replace(/[_\s]/g, '').includes('totalinternalsupply'));
    const totalExternalKey = keys.find(k => k.toLowerCase().replace(/[_\s]/g, '').includes('totalexternalsupply'));
    const gapKey = keys.find(k => k.toLowerCase() === 'gap');
    
    const totalDemand = grandTotalRow[totalDemandKey] || 0;
    const totalInternal = grandTotalRow[totalInternalKey] || 0;
    const totalExternal = grandTotalRow[totalExternalKey] || 0;
    const totalSupply = Number(totalInternal) + Number(totalExternal);
    const gap = grandTotalRow[gapKey] || 0;
    
    // Get top 3 skill towers by demand (excluding grand total)
    const otherRows = reportData.filter(row => 
      !String(row[firstColKey]).toLowerCase().includes('grand total')
    );
    const sortedByDemand = [...otherRows].sort((a, b) => 
      (Number(b[totalDemandKey]) || 0) - (Number(a[totalDemandKey]) || 0)
    );
    const top3Skills = sortedByDemand.slice(0, 3).map(row => row[firstColKey]).join(", ");
    
    const gapStatus = Number(gap) <= 0 
      ? "which is good and indicates healthy supply coverage." 
      : "which needs to be improved to meet the demand.";
    
    return `There are total ${totalDemand} demands and ${totalSupply} Supply. From that Internal Supply (PDP, VCDP and Rotation) are ${totalInternal} and External Supply (Lateral) are ${totalExternal}.\n\nThere is a gap of ${gap} ${gapStatus}\n\nTop demands are from: ${top3Skills}.`;
  };

  const handleAiClick = () => {
    setAiModalOpen(true);
    setAiText("");
    setIsTyping(true);
    
    const fullText = generateAiAnalysis();
    let index = 0;
    const totalDuration = 600; // 0.6 seconds total - fast typing
    const intervalTime = totalDuration / fullText.length;
    
    const typeInterval = setInterval(() => {
      if (index < fullText.length) {
        setAiText(fullText.substring(0, index + 1));
        index++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, intervalTime);
  };

  const handleAiModalClose = () => {
    setAiModalOpen(false);
    setAiText("");
    setIsTyping(false);
  };

  return (
    <Box sx={{ width: "100%", height: "calc(100vh - 80px)", display: "flex", flexDirection: "column" }}>
      {/* Full Screen Loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit" sx={{ textAlign: "center" }}>
            Loading Dashboard...
          </Typography>
        </Box>
      </Backdrop>

      {chartView ? (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* Header Row with Title and Toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: primaryColor,
              }}
            >
              Demand & Supply Dashboard
            </Typography>
            <FormControlLabel
              ref={toggleRef}
              control={
                <Switch
                  checked={chartView}
                  onChange={handleChartViewToggle}
                  color="primary"
                />
              }
              label="Chart View"
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DashboardCharts />
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* Single Row: Title + Filters + View Report + Chart View Toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: primaryColor,
                whiteSpace: "nowrap",
              }}
            >
              Demand & Supply Dashboard
            </Typography>
            <FilterControls onReportData={handleReportData} onLoadingChange={handleLoadingChange} />
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Export Excel">
                <IconButton size="small" sx={{ color: primaryColor }} onClick={handleExportExcel}>
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Image">
                <IconButton size="small" sx={{ color: primaryColor }} onClick={handleExportImage}>
                  <ImageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy Data">
                <IconButton size="small" sx={{ color: primaryColor }} onClick={handleCopyData}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="AI Analysis">
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: "#fff", 
                    backgroundColor: primaryColor,
                    '&:hover': { backgroundColor: '#003d7a' },
                    ml: 1 
                  }} 
                  onClick={handleAiClick}
                  disabled={!reportData}
                >
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <FormControlLabel
                ref={toggleRef}
                control={
                  <Switch
                    checked={chartView}
                    onChange={handleChartViewToggle}
                    color="primary"
                  />
                }
                label="Chart View"
                sx={{ ml: 1 }}
              />
            </Box>
            {/* Tour Tooltip */}
            <Popper open={showTour} anchorEl={toggleRef.current} placement="bottom" transition>
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <Paper sx={{ p: 2, maxWidth: 280, bgcolor: primaryColor, color: "white", position: "relative" }}>
                    <IconButton
                      size="small"
                      onClick={closeTour}
                      sx={{ position: "absolute", top: 4, right: 4, color: "white" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                      📊 Take a Tour!
                    </Typography>
                    <Typography variant="body2">
                      Enable "Chart View" to explore interactive dashboards with KPIs and visualizations!
                    </Typography>
                  </Paper>
                </Fade>
              )}
            </Popper>
          </Box>
          {/* Table Section */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {reportData && (
              <DashboardTable ref={tableRef} reportData={reportData} filterValues={filterValues} />
            )}
          </Box>
        </Box>
      )}

      {/* AI Analysis Modal */}
      <Modal
        open={aiModalOpen}
        onClose={handleAiModalClose}
        aria-labelledby="ai-analysis-modal"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: primaryColor,
              color: '#fff',
              px: 2,
              py: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                AI Analysis
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleAiModalClose} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {/* Content */}
          <Box sx={{ p: 3, minHeight: 150 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.8,
              }}
            >
              {aiText}
              {isTyping && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: 8,
                    height: 18,
                    bgcolor: primaryColor,
                    ml: 0.5,
                    animation: 'blink 0.7s infinite',
                    '@keyframes blink': {
                      '0%, 50%': { opacity: 1 },
                      '51%, 100%': { opacity: 0 },
                    },
                  }}
                />
              )}
            </Typography>
          </Box>
          {/* Footer */}
          <Box sx={{ px: 3, py: 2, bgcolor: '#f5f5f5', textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {isTyping ? "Analyzing..." : "Analysis complete"}
            </Typography>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default Dashboard;
